import React, { useState, useRef, useEffect } from "react";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import EmailEditorModal from "@/components/EmailEditorModal.jsx";
import EmailPreviewTile from "@/components/EmailPreviewTile.jsx";
import ToggleSwitch from "@/components/ToggleSwitch.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import Tooltip from "@/components/Tooltip.jsx";
import { sendBroadcastTestEmail } from "@/api/crm.js";
import { useDispatch, useSelector } from "react-redux";
import { addToast } from "../../../store/app/action.js";
import { useLocation } from "react-router-dom";

const BroadcastForm = ({ initialData = {}, onSave, onCancel, readOnly = false }) => {
  const isEdit = Boolean(initialData.name);
  const dispatch = useDispatch();
  const location = useLocation();
  const crmSettings = useSelector((state) => state.crmSettings);
  const [name, setName] = useState(initialData.name || "");
  const [description, setDescription] = useState(initialData.description || "");
  const [subject, setSubject] = useState(initialData.subject || "");
  const [previewText, setPreviewText] = useState(initialData.previewText || "");
  const [scheduledDate, setScheduledDate] = useState(initialData.scheduledDate || "");
  const [scheduledTime, setScheduledTime] = useState(initialData.scheduledTime || "");
  const [excludeHeader, setExcludeHeader] = useState(initialData.excludeHeader || false);
  const [sendAsap, setSendAsap] = useState(initialData.sendAsap || false);
  const [isPublished, setIsPublished] = useState(initialData.isPublished || false);

  // Email content state
  const [emailContent, setEmailContent] = useState(initialData.emailContent || "");
  const [emailDesign, setEmailDesign] = useState(initialData.emailDesign || null); // Store design data for drag-drop editor
  const [showEmailEditorModal, setShowEmailEditorModal] = useState(false);

  // Test email modal state
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState("");
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [hasBeenSaved, setHasBeenSaved] = useState(isEdit); // If editing, it was previously saved
  const [emailId, setEmailId] = useState(initialData.emailId || null);
  const [showEmailViewModal, setShowEmailViewModal] = useState(false);

  // Extract form submission logic to reusable function
  const handleFormSubmit = async (customEmailContent = null, customEmailDesign = null, shouldClose = true) => {
    // Validate that schedule is in the future (only if published and not sending ASAP)
    if (isPublished && !sendAsap) {
      const scheduleDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      const now = new Date();
      
      if (scheduleDateTime <= now) {
        dispatch(addToast({ type: "error", message: "Schedule to send must be in the future", timer: 6 }));
        return;
      }
    }
    
    // Generate payload with specified keys
    const payload = {
      name,
      description,
      subject,
      preview_text: previewText,
      html: customEmailContent || emailContent,
      exclude_header: excludeHeader,
      is_published: crmSettings.development_mode ? false : isPublished,
    };
    
    // Include design data
    if (customEmailDesign || emailDesign) {
      payload.design = customEmailDesign || emailDesign;
    }
    
    // Only include schedule if published and not sending ASAP
    if (isPublished && !sendAsap) {
      const scheduleDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
      payload.schedule = scheduleDateTime.toISOString();
    }
    
          try {
        if (onSave) {
          await onSave(payload, shouldClose);
          // If we reach here, the save was successful
          setHasBeenSaved(true);
        }
      } catch (error) {
        // Error handling is done in the parent component
      }
  };

  // Update hasBeenSaved when initialData changes (for async loading)
  useEffect(() => {
    if (initialData) {
      if (initialData.name) setHasBeenSaved(true);
      if (initialData.emailId) setEmailId(initialData.emailId);
    }
  }, [initialData]);

  // Prevent publishing when development mode is active
  useEffect(() => {
    if (crmSettings.development_mode && isPublished) {
      setIsPublished(false);
    }
  }, [crmSettings.development_mode, isPublished]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openTest") === "1") {
      setShowTestEmailModal(true);
    }
  }, [location.search]);

  // Email content preview block
  const renderEmailContentPreview = () => (
    <EmailPreviewTile
      emailContent={emailContent}
      emailDesign={emailDesign}
      title="Email Content"
      description="This is the content that will be sent to the customer."
      onEdit={() => readOnly ? setShowEmailViewModal(true) : setShowEmailEditorModal(true)}
      readOnly={readOnly}
      buttonText={readOnly ? 'View Email Content' : 'Edit Email Content'}
    />
  );

  const handleEmailSave = (data) => {
    setEmailContent(data.html);
    setEmailDesign(data.design);
    
    // If editing an existing broadcast, also submit the parent form
    if (isEdit) {
      handleFormSubmit(data.html, data.design, false);
    }
  };

  // Test email modal
  const renderTestEmailModal = () => (
    <ModalBasic
      modalOpen={showTestEmailModal}
      setModalOpen={setShowTestEmailModal}
      title="Send Test Email"
      width="xl"
    >
      <div className="space-y-4">
        {!hasBeenSaved || !emailId ? (
          <div className="text-center py-4">
            <div className="text-slate-600 dark:text-slate-400 mb-2">
              {(!hasBeenSaved) ? "Please save the broadcast first before sending a test email." : "This broadcast does not have an ID yet. Save it first to send a test email."}
            </div>
            <SecondaryButton
              type="button"
              onClick={() => setShowTestEmailModal(false)}
            >
              Close
            </SecondaryButton>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">
                Email Address
              </label>
              <input
                type="email"
                className="form-input w-full"
                value={testEmailAddress}
                onChange={(e) => setTestEmailAddress(e.target.value)}
                placeholder="Enter email address to send test to"
                required
              />
            </div>
            <div className="flex gap-2 justify-end">
              <SecondaryButton
                type="button"
                onClick={() => {
                  setShowTestEmailModal(false);
                  setTestEmailAddress("");
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                onClick={async () => {
                  if (!testEmailAddress.trim()) {
                    dispatch(addToast({ type: "error", message: "Please enter an email address", timer: 5 }));
                    return;
                  }
                  
                  if (!emailContent.trim()) {
                    dispatch(addToast({ type: "error", message: "Please add email content before sending a test", timer: 5 }));
                    return;
                  }
                  
                  setSendingTestEmail(true);
                  try {
                    await sendBroadcastTestEmail(emailId, testEmailAddress.trim());
                    dispatch(addToast({ type: "success", message: "Test email sent", timer: 5 }));
                    setShowTestEmailModal(false);
                    setTestEmailAddress("");
                  } catch (error) {
                    let errorMessage = "Failed to send test email. Please try again.";
                    
                    // Try to parse the error response to get the server's message
                    if (error?.response) {
                      try {
                        const responseData = e.response?.data
                        if (responseData?.message) {
                          errorMessage = responseData.message;
                        }
                      } catch (parseError) {
                      }
                    }
                    
                    dispatch(addToast({ type: "error", message: errorMessage, timer: 8 }));
                  } finally {
                    setSendingTestEmail(false);
                  }
                }}
                disabled={sendingTestEmail}
              >
                {sendingTestEmail ? "Sending..." : "Send Test Email"}
              </PrimaryButton>
            </div>
          </>
        )}
      </div>
    </ModalBasic>
  );

  // View Email Content modal
  const renderEmailViewModal = () => (
    <ModalBasic
      modalOpen={showEmailViewModal}
      setModalOpen={setShowEmailViewModal}
      title="Email Content"
      width="4xl"
    >
      <div className="border border-slate-200 dark:border-slate-700 rounded bg-white dark:bg-theme-surface-accent p-4 max-h-[70vh] overflow-auto">
        {emailContent ? (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            dangerouslySetInnerHTML={{ __html: emailContent }}
          />
        ) : (
          <div className="text-slate-400">No content defined</div>
        )}
      </div>
      <div className="flex justify-end mt-4">
        <SecondaryButton type="button" onClick={() => setShowEmailViewModal(false)}>Close</SecondaryButton>
      </div>
    </ModalBasic>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-2xl mx-auto">
      <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold mb-6">
        {readOnly ? `View Broadcast` : isEdit ? `Edit Broadcast` : `New Broadcast`}
      </h1>
      
      {crmSettings.development_mode && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-yellow-800 dark:text-yellow-200 font-medium">
              Development Mode Active
            </span>
          </div>
          <p className="text-yellow-700 dark:text-yellow-300 text-sm mt-1">
            Publishing broadcasts is disabled while development mode is enabled. You can still create and edit drafts.
          </p>
        </div>
      )}
      
      <form
        className="space-y-6"
        onSubmit={async e => {
          e.preventDefault();
          await handleFormSubmit();
        }}
      >
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">Broadcast Name</label>
          <input
            className="form-input w-full"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Enter broadcast name"
            required
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">Description</label>
          <textarea
            className="form-input w-full"
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Enter broadcast description"
            rows={3}
            disabled={readOnly}
          />
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">Email Subject</label>
          <input
            className="form-input w-full"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Enter email subject line"
            required
            disabled={readOnly}
          />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <label className="block text-slate-700 dark:text-slate-200 font-semibold">Preview Text</label>
            <Tooltip size="md" position="top">
              Preview text appears in email clients below the subject line and gives recipients a brief preview of the email content. It helps improve open rates and provides context before opening the email.
            </Tooltip>
          </div>
          <input
            className="form-input w-full"
            value={previewText}
            onChange={e => setPreviewText(e.target.value)}
            placeholder="Enter preview text (optional)"
            maxLength={150}
            disabled={readOnly}
          />
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {previewText.length}/150 characters
          </div>
        </div>
        <div>
          <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-1">Email Options</label>
          <div className="space-y-3 p-3 border border-slate-200 dark:border-slate-700 rounded bg-slate-50 dark:bg-slate-900/20">
            <div className="flex items-center gap-3">
              <ToggleSwitch
                checked={excludeHeader}
                onChange={readOnly ? () => {} : setExcludeHeader}
              />
              <span className="text-slate-700 dark:text-slate-200">Do not include default email header</span>
            </div>
            <div className="flex items-center gap-3">
              <ToggleSwitch
                checked={isPublished}
                onChange={readOnly ? () => {} : (checked) => {
                  if (crmSettings.development_mode) {
                    dispatch(addToast({ 
                      type: "warning", 
                      message: "Publishing is disabled in development mode", 
                      timer: 5 
                    }));
                    return;
                  }
                  setIsPublished(checked);
                }}
                disabled={crmSettings.development_mode}
              />
              <span className="text-slate-700 dark:text-slate-200">Publish</span>
              {crmSettings.development_mode && (
                <span className="text-xs text-red-500 dark:text-red-400 ml-2">
                  (Development mode is active. Publishing is disabled.)
                </span>
              )}
            </div>
            {isPublished && (
              <>
                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    checked={sendAsap}
                    onChange={readOnly ? () => {} : setSendAsap}
                  />
                  <span className="text-slate-700 dark:text-slate-200">Send ASAP</span>
                </div>
                {!sendAsap && (
                  <div className="pt-3 border-t border-slate-200 dark:border-slate-600">
                    <label className="block text-slate-700 dark:text-slate-200 font-semibold mb-2">Schedule to Send</label>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <label className="block text-slate-600 dark:text-slate-300 text-sm mb-1">Date</label>
                        <input
                          type="date"
                          className="form-input w-full"
                          value={scheduledDate}
                          onChange={e => setScheduledDate(e.target.value)}
                          required={isPublished && !sendAsap}
                          disabled={readOnly}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="block text-slate-600 dark:text-slate-300 text-sm mb-1">Time</label>
                        <input
                          type="time"
                          className="form-input w-full"
                          value={scheduledTime}
                          onChange={e => setScheduledTime(e.target.value)}
                          required={isPublished && !sendAsap}
                          disabled={readOnly}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {renderEmailContentPreview()}
        <div className="flex gap-2 justify-end">
          <SecondaryButton type="button" onClick={onCancel}>Close</SecondaryButton>
          <SecondaryButton 
            type="button" 
            onClick={() => setShowTestEmailModal(true)}
            disabled={!hasBeenSaved || !emailId || !emailContent.trim()}
            title={!hasBeenSaved ? "Please save the broadcast first before sending a test email" : !emailId ? "Broadcast ID is missing. Save the broadcast first." : !emailContent.trim() ? "Please add email content before sending a test email" : ""}
          >
            Send Test Email
          </SecondaryButton>
          {!readOnly && (<PrimaryButton type="submit">Save Broadcast</PrimaryButton>)}
        </div>
      </form>
      <EmailEditorModal
        isOpen={showEmailEditorModal}
        onClose={() => setShowEmailEditorModal(false)}
        onSave={handleEmailSave}
        initialDesign={emailDesign}
        title="Edit Email Content"
      />
      {renderTestEmailModal()}
      {renderEmailViewModal()}
    </div>
  );
};

export default BroadcastForm; 